import { Component, HostListener, Input, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { filter } from 'rxjs';

import { IMenuItem } from '../../../interfaces/IMenu.interface';

@Component({
  selector: 'g99-lib-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrl: './nav-menu.component.scss',
  standalone: false,
})
export class NavMenuComponent implements OnInit {
  @Input() public menuItems: IMenuItem[] = [];

  protected isMobile = false;
  protected hasCompactMenu = false; // Track if menu should be compact
  protected screenSize: 'large' | 'medium' | 'small' | 'mobile' = 'large';

  constructor(private readonly _router: Router) {}

  @HostListener('window:resize', [])
  public onResize(): void {
    this.updateViewMode();
  }

  ngOnInit(): void {
    this.updateViewMode();
    this.checkMenuLength();
    // Set initial active state based on current URL
    this.setActiveMenuItems(this._router.url);

    // Listen to route changes to update active state
    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.setActiveMenuItems(event.url);
      });
  }

  protected updateViewMode(): void {
    const width = window.innerWidth;
    this.isMobile = width <= 1023;

    // Determine screen size for styling
    if (width <= 1023) {
      this.screenSize = 'mobile';
    } else if (width <= 1200) {
      this.screenSize = 'small';
    } else if (width <= 1400) {
      this.screenSize = 'medium';
    } else {
      this.screenSize = 'large';
    }
  }

  protected checkMenuLength(): void {
    // Check if parent level menu items exceed 8
    this.hasCompactMenu = this.menuItems.length > 8;
  }

  protected setActiveMenuItems(currentRoute: string) {
    // Reset all active states
    this.resetActiveState(this.menuItems);

    // Set active state for matching routes
    this.setActiveStateForRoute(this.menuItems, currentRoute);
  }

  protected resetActiveState(menuItems: MenuItem[]) {
    menuItems.forEach((item) => {
      item['isActive'] = false;
      if (item['submenu']) {
        this.resetActiveState(item['submenu']);
      }
    });
  }

  protected setActiveStateForRoute(
    menuItems: MenuItem[],
    currentRoute: string
  ) {
    for (const item of menuItems) {
      const normalizedRoute = item['route'].startsWith('/')
        ? item['route']
        : `/${item['route']}`;

      if (currentRoute.startsWith(normalizedRoute) && item['route'] !== '') {
        item['isActive'] = true;
        return true;
      }

      if (item['submenu']) {
        const isSubmenuActive = this.setActiveStateForRoute(
          item['submenu'],
          currentRoute
        );
        if (isSubmenuActive) {
          item['isActive'] = true;
          return true;
        }
      }
    }
    return false;
  }

  protected navigateTo(item: MenuItem, event: MouseEvent) {
    if (this.isMobile) {
      // In mobile mode, if item has submenu, toggle expand
      if (item['submenu']) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleMobileSubmenu(item);
      } else if (item['route']) {
        // Navigate only for items with route
        this._router.navigate([item['route']]);
        // this.mobileMenuOpen = false;
      }
    } else {
      // Desktop behavior - navigate if has route
      if (item['route']) {
        this._router.navigate([item['route']]);
        // Close all menus
        this.menuItems.forEach((menuItem) => {
          menuItem.isHovered = false;
        });
      }
      event.stopPropagation();
    }
  }

  protected toggleMobileSubmenu(item: MenuItem) {
    item['isExpanded'] = !item['isExpanded'];
  }

  protected onMouseEnter(item: MenuItem) {
    item['isHovered'] = true;
  }

  protected onMouseLeave(item: MenuItem) {
    item['isHovered'] = false;
  }

  protected isItemActive(item: MenuItem): boolean {
    return item['isActive'] || false;
  }
}
